import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { PageProps } from 'gatsby'
import clsx from 'clsx'
import moment, { Moment } from 'moment'
import {
  Alert,
  ArrowButton,
  Button,
  Card,
  DatePicker,
  Error,
  Form,
  Icon,
  Input,
  Paragraph,
  SEO,
  showMessage,
  Space,
  Title,
} from 'components/common'
import Layout from 'components/layouts/tow-column'
import {
  HolidayDocument,
  useHolidayCreateMutation,
  useHolidayQuery,
  useHolidayUpdateMutation,
} from 'lib/graphql/storeadmin'

type Holiday = {
  date: Moment | string
  reason: string
  isPublic: boolean
  isClose: boolean
}
type FormValues = {
  holidays: Holiday[]
}

const DEFAULT_HOLIDAYS = [
  { date: '01-01', reason: '元日' },
  { date: null, reason: '成人の日' },
  { date: null, reason: '建国記念日' },
  { date: '02-23', reason: '天皇誕生日' },
  { date: null, reason: '春分の日' },
  { date: '04-29', reason: '昭和の日' },
  { date: '05-03', reason: '憲法記念日' },
  { date: '05-04', reason: 'みどりの日' },
  { date: '05-05', reason: 'こどもの日' },
  { date: null, reason: '海の日' },
  { date: '08-11', reason: '山の日' },
  { date: null, reason: '敬老の日' },
  { date: null, reason: '秋分の日' },
  { date: null, reason: 'スポーツの日' },
  { date: '11-03', reason: '文化の日' },
  { date: '11-23', reason: '勤労感謝の日' },
]

const YEAR_FORMAT = 'YYYY'
const DATE_FORMAT = 'M/D'
const currentYear = new Date().getFullYear()
const todayDate = `${new Date().getMonth() + 1}-${new Date().getDate()}`

export const Head = () => <SEO title="祝日設定" />

const IndexPage = (props: PageProps<Queries.Query>): JSX.Element => {
  const [requesting, setRequesting] = useState(false)
  const [year, setYear] = useState(currentYear)
  const [form] = Form.useForm<FormValues>()
  const firstDateOfYear = `${year}-01-01`
  const firstDateOfNextYear = `${year + 1}-01-01`
  // holiday error useState
  const [showHolidayErrorAlert, setShowHolidayErrorAlert] = useState(false)
  const [holidayErrorDescription, setHolidayErrorDescription] = useState([])

  // get current holidays
  const { data, error, refetch, loading } = useHolidayQuery({
    variables: { year },
  })
  // create holidays
  const [createHoliday] = useHolidayCreateMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: HolidayDocument, variables: { year } }],
  })
  // update holidays
  const [updateHoliday] = useHolidayUpdateMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: HolidayDocument, variables: { year } }],
  })

  // デフォルトの祝日一覧
  const defaultHolidays = useMemo(() => {
    return DEFAULT_HOLIDAYS.map(({ date, reason }) => ({
      date: typeof date === 'string' ? moment(`${year}-${date}`) : date,
      reason,
      isPublic: true,
      isClose: false,
    }))
  }, [year])
  // サーバーから取得した祝日一覧
  const holidays = useMemo(() => {
    return data?.holiday?.holidays?.edges?.map(({ node }) => ({
      date: moment(node.date),
      reason: node.reason.jaJp,
      isPublic: true,
      isClose: false,
    }))
  }, [data])

  const holidaysMemo = useMemo(() => {
    return holidays || defaultHolidays
  }, [defaultHolidays, holidays])

  useEffect(() => {
    if (loading === false) {
      form.setFieldsValue({
        holidays: holidaysMemo,
      })
    }
  }, [holidaysMemo, form, loading])

  // change year
  const changeYear = (params: string) => {
    switch (params) {
      case 'previous':
        if (year >= currentYear) setYear(year - 1)
        return
      case 'next':
        setYear(year + 1)
        return
    }
  }
  const dateOnChange = (date: Moment, dateString: string) => {
    if (Number(dateString) >= currentYear) return setYear(Number(dateString))
  }

  useEffect(() => {
    refetch({ year })
  }, [refetch, year])

  const holidayDateFormat = useCallback(
    (date) => {
      return date?.format(`${year}-MM-DD`)
    },
    [year]
  )

  const onFinish = async ({ holidays }: FormValues) => {
    holidays = holidays
      ? holidays.map((holiday) => ({
          ...holiday,
          date: holidayDateFormat(moment(holiday?.date)),
        }))
      : []
    try {
      setRequesting(true)

      let holidayData
      if (data.holiday === null) {
        const { data } = await createHoliday({
          variables: {
            input: {
              holidays,
            },
          },
        })
        holidayData = data
      } else {
        const { data } = await updateHoliday({
          variables: {
            year,
            input: {
              holidays,
            },
          },
        })
        holidayData = data
      }
      const userErrors = holidayData?.holidayUserErrors || []
      if (userErrors.length > 0) {
        showMessage('error', 'エラーが発生しました。')
        setShowHolidayErrorAlert(true)
        const errorMessages = []
        userErrors.forEach((value) => {
          switch (value.code) {
            case 'BLANK':
              errorMessages.push('入力値が空白です。')
              break
            case 'INVALID':
              if (value.field.indexOf('date') !== -1) {
                errorMessages.push('日付を確認してください。')
              } else if (value.field.indexOf('reason') !== -1) {
                errorMessages.push('名称を確認してください。')
              } else {
                errorMessages.push('入力値が不正です。')
              }
              break
            case 'NOT_FOUND':
              errorMessages.push('ストアは存在しません。')
              break
            case 'TAKEN':
              if (value.field.indexOf('date') !== -1) {
                errorMessages.push('日付の価値はすでに取得されています。')
              } else if (value.field.indexOf('reason') !== -1) {
                errorMessages.push('名称の価値はすでに取得されています。')
              } else {
                errorMessages.push('入力値はすでに取得されています。')
              }
              break
            case 'TOO_SHORT' || 'TOO_LONG':
              if (value.field.indexOf('date') !== -1) {
                errorMessages.push('日付を確認してください。')
              } else if (value.field.indexOf('reason') !== -1) {
                errorMessages.push('名称を確認してください。')
              } else {
                errorMessages.push(
                  '入力値が長すぎたり短すぎたりしないことを確認してください'
                )
              }
              break
            case undefined:
              errorMessages.push(...errorMessages)
              break
            default:
              errorMessages.push('エラーが発生しました。')
              break
          }
        })
        const filteredErrorMessage = errorMessages?.filter(
          (e, i) => errorMessages?.indexOf(e) === i
        )
        setHolidayErrorDescription(filteredErrorMessage)
      } else if (error) {
        showMessage('error', 'エラーが発生しました。')
      } else {
        setShowHolidayErrorAlert(false)
        showMessage('success', 'データを更新しました。')
      }
    } catch (e) {
      setShowHolidayErrorAlert(true)
      showMessage('error', 'サーバーとの通信時にエラーが発生しました。')
    } finally {
      setRequesting(false)
    }
  }

  const onCloseAlert = () => {
    setShowHolidayErrorAlert(false)
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <Form
        form={form}
        component="form"
        requiredMark={false}
        onFinish={onFinish}
      >
        <Layout
          title={`祝日設定 ${year}年`}
          loading={loading}
          actions={
            <Button.Group>
              <ArrowButton
                direction="left"
                disabled={year <= currentYear}
                onClick={() => changeYear('previous')}
              />
              <DatePicker
                className={clsx('mx-2', 'bg-white', 'w-32')}
                allowClear={false}
                picker="year"
                format={YEAR_FORMAT}
                superNextIcon={null}
                superPrevIcon={null}
                onChange={dateOnChange}
                value={moment(year, YEAR_FORMAT)}
                disabledDate={(current) => {
                  return current && current < moment().subtract(0, 'years')
                }}
              />
              <ArrowButton
                direction="right"
                onClick={() => changeYear('next')}
              />
            </Button.Group>
          }
        >
          <Layout.Section size="small">
            <Layout.Item>
              <Title level={4} style={{ paddingTop: '24px' }}>
                祝日設定
              </Title>
              <Paragraph>
                予約時のカレンダーに表示される祝日を設定します。
              </Paragraph>
              <Paragraph>
                日付が固定の祝日は初めから設定されていますが、移動祝日は名前左横に日付を設定してください。
              </Paragraph>
              <Paragraph>
                また、祝日を追加する場合は「祝日を追加する」を押下してください。
              </Paragraph>
              <Paragraph>
                削除する場合は、日付と名称を共に空白にして「保存する」を押下してください。
              </Paragraph>
            </Layout.Item>
          </Layout.Section>

          <Layout.Section size="large">
            <Layout.Item>
              {showHolidayErrorAlert && (
                <Alert
                  className={clsx('mb-5')}
                  message="エラー"
                  description={
                    <div>
                      {holidayErrorDescription.length > 0 &&
                        holidayErrorDescription?.map((value, index) => (
                          <>
                            <p key={index.toString()}>
                              {index + 1}: {value}
                            </p>
                          </>
                        ))}
                    </div>
                  }
                  type="error"
                  showIcon
                  closable
                  onClose={onCloseAlert}
                />
              )}
              <Card shadow loading={loading}>
                <Form.List name="holidays" initialValue={holidaysMemo}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Fragment key={key}>
                          <Space
                            className={clsx('flex', 'mb-0.5')}
                            align="baseline"
                          >
                            <Form.Item
                              {...restField}
                              label={index === 0 && '日付'}
                              name={[name, 'date']}
                              fieldKey={[index, 'date']}
                              rules={[
                                {
                                  required: true,
                                  message: '日付は必須です。',
                                },
                              ]}
                            >
                              <DatePicker
                                placeholder="日付を選択してください"
                                format={DATE_FORMAT}
                                superNextIcon={null}
                                superPrevIcon={null}
                                defaultPickerValue={
                                  holidaysMemo[index]?.date !== null &&
                                  index < holidaysMemo.length
                                    ? moment(`${holidaysMemo[index]?.date}`)
                                    : moment(`${year}-${todayDate}`)
                                }
                                disabledDate={(current) => {
                                  return (
                                    current < moment(firstDateOfYear) ||
                                    current >= moment(firstDateOfNextYear)
                                  )
                                }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label={index === 0 && '名称'}
                              name={[name, 'reason']}
                              fieldKey={[index, 'reason']}
                              rules={[
                                {
                                  required: true,
                                  message: '名称は必須です。',
                                },
                              ]}
                            >
                              <Input
                                autoComplete="off"
                                placeholder="祝日名を入力してください"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'isPublic']}
                              fieldKey={[index, 'isPublic']}
                              hidden
                              initialValue={true}
                            >
                              <Input />
                            </Form.Item>
                            {index + 1 > DEFAULT_HOLIDAYS.length ? (
                              <Button
                                icon={<Icon.DeleteFilled />}
                                onClick={() => remove(name)}
                              />
                            ) : (
                              <Button />
                            )}
                            <Form.Item
                              {...restField}
                              name={[name, 'isClose']}
                              fieldKey={[index, 'isClose']}
                              hidden
                              initialValue={false}
                            >
                              <Input />
                            </Form.Item>
                          </Space>
                        </Fragment>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<Icon.PlusOutlined />}
                        >
                          祝日を追加する
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Card>
            </Layout.Item>

            <div className={clsx('flex', 'my-10', 'justify-end')}>
              <Button
                type="primary"
                htmlType="submit"
                loading={requesting}
                disabled={loading}
              >
                保存する
              </Button>
            </div>
          </Layout.Section>
        </Layout>
      </Form>
    </>
  )
}

export default IndexPage
